import { IAccessoryDraft } from '../interfaces/heavyDutyMachinery/IAccessoryAssist';
import { isBlankFormValue } from './machineryDraftHelper';

/**
 * Turning what the accessory assistant suggested into values the modal's form
 * understands.
 *
 * The sibling of applyMachineryDraft and governed by the same rule, which is
 * the one worth testing away from any screen: a suggestion never replaces an
 * answer the host has already given. What is different here is that the form
 * opens with two switches already set -- and a switch nobody has touched is
 * not an answer, which is exactly the distinction that decides whether the
 * hydraulic half of this form ever appears.
 */

/**
 * The field whose suggestion carries a consequence somebody else pays for.
 *
 * Only one, and that restraint is the point: half a form in coral is a form
 * nobody reads. Which machines an attachment fits is the field that decides
 * whether a guest turns up with a breaker that will not go on their
 * excavator; everything else here is wrong on a screen.
 */
export const ACCESSORY_CHECK_FIELDS = ['compatibleCategories'];

export interface AccessoryDraftApplication {
  /** Partial form values, already excluding anything the host had answered. */
  values: Record<string, unknown>;
  /** Every field name this draft set, so the screen can mark what has to be checked. */
  filledFields: string[];
}

/**
 * @param current the form store as it stands (getFieldsValue(true)).
 * @param defaults what the modal put there when it opened -- a field still
 *        holding one of these has not been answered by anybody. Without it,
 *        `requiresHydraulics: false` from opening the modal would read as a
 *        deliberate "no", and a breaker's whole hydraulic section would stay
 *        empty and hidden.
 */
export const applyAccessoryDraft = (
  draft: IAccessoryDraft,
  current: Record<string, unknown>,
  defaults: Record<string, unknown> = {}
): AccessoryDraftApplication => {
  const values: Record<string, unknown> = {};
  const filledFields: string[] = [];

  const offer = (field: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;

    const held = current[field];
    const stillOurDefault = Object.prototype.hasOwnProperty.call(defaults, field) && held === defaults[field];
    if (!isBlankFormValue(held) && !stillOurDefault) return;
    if (held === value) return;

    values[field] = value;
    filledFields.push(field);
  };

  offer('name', draft.name);
  offer('description', draft.description);

  // The one field this whole feature is worth the most for. An empty list is
  // a real answer -- the assistant could not tell what carries it -- and is
  // left for the host rather than offered as nothing.
  if (draft.compatibleCategories.length > 0 && isBlankFormValue(current.compatibleCategories)) {
    values.compatibleCategories = draft.compatibleCategories;
    filledFields.push('compatibleCategories');
  }

  offer('weightRangeMinTons', draft.weightRangeMinTons);
  offer('weightRangeMaxTons', draft.weightRangeMaxTons);
  offer('couplerType', draft.couplerType);

  offer('requiresHydraulics', draft.requiresHydraulics);
  offer('hydraulicFlowMinLMin', draft.hydraulicFlowMinLMin);
  offer('hydraulicFlowMaxLMin', draft.hydraulicFlowMaxLMin);
  offer('hydraulicPressureMinBar', draft.hydraulicPressureMinBar);
  offer('hydraulicPressureMaxBar', draft.hydraulicPressureMaxBar);
  offer('hydraulicLines', draft.hydraulicLines);
  offer('requiresHighFlow', draft.requiresHighFlow);
  offer('requiresCaseDrain', draft.requiresCaseDrain);
  offer('hydraulicConnectorType', draft.hydraulicConnectorType);

  offer('workingWidthMm', draft.workingWidthMm);
  offer('capacityLiters', draft.capacityLiters);
  offer('ownWeightKg', draft.ownWeightKg);

  return { values, filledFields };
};
